'use client'

import ElectionBannerTop from '@page/election/_components/ElectionBanner/ElectionBannerTop'
import { ElectionMetaResponseVo } from '@schemas/non-auth'

import { pxToRem } from '@ui/style/muiTheme'
import { Box } from '@mui/joy'
import UIText from '@components/ui/UIText'
import color from '@ui/style/color.theme'
import UIFlex from '@components/ui/UIFlex'
import DimdLayer from './DimdLayer'

interface VoteBannerProps {
	data: ElectionMetaResponseVo
	viewPoint: string
}

const VoteBanner = ({ data, viewPoint }: VoteBannerProps) => {
	const candidateImageMap: Record<string, string> = {
		이재명: 'url(/images/election/banner/candidate_person1.png)',
		김문수: 'url(/images/election/banner/candidate_person2.png)',
		이준석: 'url(/images/election/banner/candidate_person3.png)',
		권영국: 'url(/images/election/banner/candidate_person4.png)',
	}

	const getCandidateImage = (candidateName: string) => candidateImageMap[candidateName]

	const candidate1 = getCandidateImage(data.topBannerChart.voteChart.chartData[0]?.candidateName)
	const candidate2 = getCandidateImage(data.topBannerChart.voteChart.chartData[1]?.candidateName)

	const resultValueType = data.topBannerChart.voteChart.chartData[0]?.resultValue

	const badgeImageMap: Record<string, string> = {
		'1': 'url(/images/election/banner/badge_elected.png)',
		'2': 'url(/images/election/banner/badge_certain.png)',
		'3': 'url(/images/election/banner/badge_potent.png)',
	}

	const badgeImage = badgeImageMap[resultValueType]

	const getChartData = (index: number) => {
		const { isError, chartData } = data.topBannerChart.voteChart
		if (isError) {
			return {
				color: '#2E6782',
				politicalPartyName: '-',
				candidateName: '-',
				resultRate: '00.00',
				resultCount: '0',
			}
		}
		return {
			color: chartData[index].color,
			politicalPartyName: chartData[index].politicalPartyName,
			candidateName: chartData[index].candidateName,
			resultRate: chartData[index].resultRate,
			resultCount: chartData[index].resultCount,
		}
	}

	const chart1 = getChartData(0)
	const chart2 = getChartData(1)

	const chartColor1 = chart1.color
	const chartColor2 = chart2.color
	const chartPoliticalPartyName1 = chart1.politicalPartyName
	const chartPoliticalPartyName2 = chart2.politicalPartyName
	const chartCandidateName1 = chart1.candidateName
	const chartCandidateName2 = chart2.candidateName
	const chartResultRate1 = chart1.resultRate
	const chartResultRate2 = chart2.resultRate
	const chartResultCount1 = chart1.resultCount
	const chartResultCount2 = chart2.resultCount
	const chartGapCount = data.topBannerChart.voteChart.isError
		? '0'
		: data.topBannerChart.voteChart.gapCount

	const totalRate = Number(chart1.resultRate) + Number(chart2.resultRate)
	const recalculatedRate1 = ((Number(chart1.resultRate) / totalRate) * 100).toFixed(2)
	const recalculatedRate2 = ((Number(chart2.resultRate) / totalRate) * 100).toFixed(2)

	return (
		<>
			{data.topBannerChart.voteChart.isError && <DimdLayer />}
			<ElectionBannerTop data={data} />
			<Box
				sx={{
					position: 'relative',
					marginTop: {
						xs: viewPoint === 'article' ? pxToRem(39) : pxToRem(140),
						md: pxToRem(13),
						lg: pxToRem(31),
						xl: pxToRem(28),
					},
				}}
			>
				<UIText
					sx={{
						position: 'absolute',
						left: '50%',
						top: {
							xs: pxToRem(-87),
							md: pxToRem(-11),
							lg: pxToRem(-27),
							xl: pxToRem(-20),
						},
						transform: 'translateX(-50%)',
						fontSize: { xs: pxToRem(28), lg: pxToRem(32) },
						lineHeight: { xs: pxToRem(28), lg: pxToRem(32) },
						color: '#2E6782',
						fontWeight: 800,
						textAlign: 'center',
						display: {
							xs: viewPoint === 'article' ? 'none' : 'block',
							md: 'block',
						},
					}}
				>
					개표현황
				</UIText>
				<UIText
					sx={{
						position: 'absolute',
						left: '50%',
						top: {
							md: pxToRem(58),
							lg: pxToRem(46),
							xl: pxToRem(70),
						},
						transform: 'translateX(-50%)',
						fontSize: {
							md: pxToRem(24),
							lg: pxToRem(36),
						},
						lineHeight: {
							md: pxToRem(24),
							lg: pxToRem(36),
						},
						color: '#082B4F66',
						opacity: '0.4',
						fontWeight: 800,
						display: {
							xs: 'none',
							md: 'block',
						},
					}}
				>
					VS
				</UIText>
				<UIFlex
					flexDirection={{
						xs: 'row',
						md: 'column',
					}}
					alignItems="center"
					sx={{
						position: 'absolute',
						left: '50%',
						top: {
							xs: pxToRem(-51),
							md: pxToRem(91),
							lg: pxToRem(103),
							xl: pxToRem(140),
						},
						transform: 'translateX(-50%)',
						display: {
							xs: viewPoint === 'article' ? 'none' : 'flex',
							md: 'flex',
						},
					}}
				>
					<UIText
						sx={{
							fontSize: {
								xs: pxToRem(13),
								lg: pxToRem(16),
							},
							lineHeight: {
								xs: pxToRem(17),
								lg: pxToRem(20),
							},
							color: '#2E6782',
							marginRight: {
								xs: pxToRem(3),
								md: pxToRem(0),
							},
						}}
					>
						득표차
					</UIText>
					<UIText
						sx={{
							fontSize: {
								xs: pxToRem(13),
								lg: pxToRem(16),
							},
							lineHeight: {
								xs: pxToRem(17),
								lg: pxToRem(20),
							},
							color: '#2E6782',
							fontWeight: 700,
						}}
					>
						{chartGapCount} 표
					</UIText>
				</UIFlex>
				<UIFlex flexDirection="row" gap={pxToRem(2)} justifyContent="between">
					<Box
						sx={{
							position: 'relative',
							width: '50%',
						}}
					>
						<UIFlex
							flexDirection="row"
							justifyContent="flex-start"
							gap={{
								xs: 0,
								md: pxToRem(2),
								lg: pxToRem(10),
								xl: pxToRem(8),
							}}
						>
							<Box
								sx={{
									position: 'relative',
									width: {
										xs: viewPoint === 'article' ? pxToRem(102) : pxToRem(132),
										md: pxToRem(162),
										lg: pxToRem(188),
										xl: pxToRem(232),
									},
									height: {
										xs: viewPoint === 'article' ? pxToRem(87) : pxToRem(112),
										md: pxToRem(137),
										lg: pxToRem(159),
										xl: pxToRem(196),
									},
									backgroundImage: data.topBannerChart.voteChart.isError
										? ''
										: candidate1,
									backgroundSize: 'cover',
									backgroundPosition: 'center',
									marginLeft: {
										xs: viewPoint === 'article' ? pxToRem(3) : pxToRem(-16),
										md: pxToRem(26),
										lg: pxToRem(32),
										xl: pxToRem(50),
									},
								}}
							>
								{badgeImage && (
									<Box
										sx={{
											position: 'absolute',
											left: {
												xs:
													viewPoint === 'article'
														? pxToRem(9)
														: pxToRem(46),
												md: pxToRem(-14),
												lg: pxToRem(-24),
												xl: pxToRem(-14),
											},
											top: {
												xs:
													viewPoint === 'article'
														? pxToRem(-10)
														: pxToRem(-52),
												md: pxToRem(32),
												lg: pxToRem(34),
												xl: pxToRem(50),
											},
											width: {
												xs:
													viewPoint === 'article'
														? pxToRem(32)
														: pxToRem(44),
												lg: pxToRem(60),
											},
											height: {
												xs:
													viewPoint === 'article'
														? pxToRem(32)
														: pxToRem(44),
												lg: pxToRem(60),
											},
											backgroundImage: badgeImage,
											backgroundSize: 'cover',
											boxShadow: '0px 6px 10px 0px rgba(0, 0, 0, 0.12)',
											borderRadius: pxToRem(60),
										}}
									/>
								)}
							</Box>
							<UIFlex
								flexDirection="column"
								alignItems="center"
								gap={{
									xs: pxToRem(8),
									lg: pxToRem(10),
									xl: pxToRem(12),
								}}
								sx={{
									marginTop: {
										xs: viewPoint === 'article' ? pxToRem(11) : pxToRem(6),
										md: pxToRem(23),
										lg: pxToRem(9),
										xl: pxToRem(28),
									},
									marginLeft: {
										xs: pxToRem(-6),
										md: 0,
									},
								}}
							>
								<UIFlex
									justifyContent="center"
									alignItems="center"
									sx={{
										backgroundColor: '#082B4F',
										borderRadius: pxToRem(100),
										padding: `${pxToRem(4)} ${pxToRem(14)}`,
									}}
								>
									<UIText
										sx={{
											fontSize: pxToRem(12),
											lineHeight: pxToRem(12),
											fontWeight: 700,
											color: color.colWhite,
										}}
									>
										1위
									</UIText>
								</UIFlex>
								<UIFlex flexDirection="column" gap={pxToRem(4)} alignItems="center">
									<UIText
										sx={{
											fontSize: {
												xs: pxToRem(12),
												lg: pxToRem(14),
												xl: pxToRem(16),
											},
											lineHeight: {
												xs: pxToRem(12),
												lg: pxToRem(14),
												xl: pxToRem(16),
											},
											color: '#082B4F',
										}}
									>
										{chartPoliticalPartyName1}
									</UIText>
									<UIText
										sx={{
											fontSize: {
												xs: pxToRem(20),
												md: pxToRem(22),
												lg: pxToRem(32),
												xl: pxToRem(36),
											},
											lineHeight: {
												xs: pxToRem(20),
												md: pxToRem(22),
												lg: pxToRem(32),
												xl: pxToRem(36),
											},
											fontWeight: 800,
											color: '#082B4F',
										}}
									>
										{chartCandidateName1}
									</UIText>
								</UIFlex>
								<UIText
									sx={{
										fontSize: {
											xs: pxToRem(20),
											md: pxToRem(28),
											lg: pxToRem(44),
											xl: pxToRem(52),
										},
										lineHeight: {
											xs: pxToRem(20),
											md: pxToRem(28),
											lg: pxToRem(44),
											xl: pxToRem(52),
										},
										color: chartColor1,
										fontWeight: 800,
										display: {
											xs: viewPoint === 'article' ? 'none' : 'block',
											md: 'block',
										},
									}}
								>
									{chartResultRate1}
									<UIText
										sx={{
											fontSize: {
												xs: pxToRem(12),
												md: pxToRem(14),
												lg: pxToRem(24),
												xl: pxToRem(24),
											},
											lineHeight: {
												xs: pxToRem(14),
												md: pxToRem(18),
												lg: pxToRem(32),
												xl: pxToRem(36),
											},
											color: chartColor1,
											fontFeatureSettings: 'case',
											marginLeft: {
												xs: pxToRem(2),
												md: pxToRem(4),
											},
										}}
									>
										%
									</UIText>
								</UIText>
							</UIFlex>
						</UIFlex>
					</Box>
					<Box
						sx={{
							position: 'relative',
							width: '50%',
						}}
					>
						<UIFlex flexDirection="row" justifyContent="flex-end">
							<UIFlex
								flexDirection="column"
								alignItems="center"
								gap={{
									xs: pxToRem(8),
									lg: pxToRem(10),
									xl: pxToRem(12),
								}}
								sx={{
									marginTop: {
										xs: viewPoint === 'article' ? pxToRem(11) : pxToRem(6),
										md: pxToRem(23),
										lg: pxToRem(9),
										xl: pxToRem(28),
									},
									marginRight: {
										xs: pxToRem(-6),
										md: 0,
									},
								}}
							>
								<UIFlex
									justifyContent="center"
									alignItems="center"
									sx={{
										border: `1px solid #082B4F`,
										borderRadius: pxToRem(100),
										padding: `${pxToRem(4)} ${pxToRem(14)}`,
									}}
								>
									<UIText
										sx={{
											fontSize: pxToRem(12),
											lineHeight: pxToRem(10),
											fontWeight: 700,
											color: '#082B4F',
										}}
									>
										2위
									</UIText>
								</UIFlex>
								<UIFlex flexDirection="column" gap={pxToRem(4)} alignItems="center">
									<UIText
										sx={{
											fontSize: {
												xs: pxToRem(12),
												lg: pxToRem(14),
												xl: pxToRem(16),
											},
											lineHeight: {
												xs: pxToRem(12),
												lg: pxToRem(14),
												xl: pxToRem(16),
											},
											color: '#082B4F',
										}}
									>
										{chartPoliticalPartyName2}
									</UIText>
									<UIText
										sx={{
											fontSize: {
												xs: pxToRem(20),
												md: pxToRem(22),
												lg: pxToRem(32),
												xl: pxToRem(36),
											},
											lineHeight: {
												xs: pxToRem(20),
												md: pxToRem(22),
												lg: pxToRem(32),
												xl: pxToRem(36),
											},
											fontWeight: 800,
											color: '#082B4F',
										}}
									>
										{chartCandidateName2}
									</UIText>
								</UIFlex>
								<UIText
									sx={{
										fontSize: {
											xs: pxToRem(20),
											md: pxToRem(28),
											lg: pxToRem(44),
											xl: pxToRem(52),
										},
										lineHeight: {
											xs: pxToRem(20),
											md: pxToRem(28),
											lg: pxToRem(44),
											xl: pxToRem(52),
										},
										color: chartColor2,
										fontWeight: 800,
										display: {
											xs: viewPoint === 'article' ? 'none' : 'block',
											md: 'block',
										},
									}}
								>
									{chartResultRate2}
									<UIText
										sx={{
											fontSize: {
												xs: pxToRem(12),
												md: pxToRem(14),
												lg: pxToRem(24),
												xl: pxToRem(24),
											},
											lineHeight: {
												xs: pxToRem(14),
												md: pxToRem(18),
												lg: pxToRem(32),
												xl: pxToRem(36),
											},
											color: chartColor2,
											fontFeatureSettings: 'case',
											marginLeft: {
												xs: pxToRem(2),
												md: pxToRem(4),
											},
										}}
									>
										%
									</UIText>
								</UIText>
							</UIFlex>
							<Box
								sx={{
									position: 'relative',
									width: {
										xs: viewPoint === 'article' ? pxToRem(102) : pxToRem(132),
										md: pxToRem(162),
										lg: pxToRem(188),
										xl: pxToRem(232),
									},
									height: {
										xs: viewPoint === 'article' ? pxToRem(87) : pxToRem(112),
										md: pxToRem(137),
										lg: pxToRem(159),
										xl: pxToRem(196),
									},
									backgroundImage: data.topBannerChart.voteChart.isError
										? ''
										: candidate2,
									backgroundSize: 'cover',
									backgroundPosition: 'center',
									marginRight: {
										xs: viewPoint === 'article' ? pxToRem(3) : pxToRem(-16),
										md: pxToRem(26),
										lg: pxToRem(32),
										xl: pxToRem(50),
									},
								}}
							/>
						</UIFlex>
					</Box>
				</UIFlex>
				<UIFlex flexDirection="row" gap={pxToRem(2)} justifyContent="between">
					<Box
						sx={{
							position: 'relative',
							width: `${recalculatedRate1}%`,
						}}
					>
						<Box
							sx={{
								width: '100%',
								position: 'relative',
								height: pxToRem(32),
								backgroundColor: chartColor1,
								textAlign: 'center',
							}}
						>
							<UIText
								sx={{
									width: 'auto',
									position: 'absolute',
									left: pxToRem(20),
									top: pxToRem(6),
									fontSize: pxToRem(20),
									lineHeight: pxToRem(20),
									color: color.colWhite,
									wordBreak: 'keep-all',
									fontWeight: 800,
									display: {
										xs: viewPoint === 'article' ? 'block' : 'none',
										md: 'none',
									},
								}}
							>
								{chartResultRate1}
								<UIText
									sx={{
										fontSize: {
											xs: pxToRem(12),
										},
										lineHeight: {
											xs: pxToRem(14),
										},
										marginLeft: pxToRem(2),
										fontWeight: 700,
										color: color.colWhite,
									}}
								>
									%
								</UIText>
							</UIText>
							<UIText
								sx={{
									width: 'auto',
									position: 'absolute',
									left: pxToRem(20),
									top: pxToRem(6),
									fontSize: pxToRem(14),
									lineHeight: pxToRem(20),
									color: color.colWhite,
									wordBreak: 'keep-all',
									display: {
										xs: viewPoint === 'article' ? 'none' : 'block',
										md: 'block',
									},
									opacity: 0.8,
								}}
							>
								{chartResultCount1} 표
							</UIText>
						</Box>
					</Box>
					<Box
						sx={{
							position: 'relative',
							width: `${recalculatedRate2}%`,
						}}
					>
						<Box
							sx={{
								width: '100%',
								position: 'relative',
								height: pxToRem(32),
								backgroundColor: chartColor2,
								textAlign: 'center',
							}}
						>
							<UIText
								sx={{
									width: 'auto',
									position: 'absolute',
									right: pxToRem(20),
									top: pxToRem(6),
									fontSize: pxToRem(20),
									lineHeight: pxToRem(20),
									color: color.colWhite,
									wordBreak: 'keep-all',
									whiteSpace: 'nowrap',
									fontWeight: 800,
									display: {
										xs: viewPoint === 'article' ? 'block' : 'none',
										md: 'none',
									},
								}}
							>
								{chartResultRate2}
								<UIText
									sx={{
										fontSize: {
											xs: pxToRem(12),
										},
										lineHeight: {
											xs: pxToRem(14),
										},
										marginLeft: pxToRem(2),
										fontWeight: 700,
										color: color.colWhite,
									}}
								>
									%
								</UIText>
							</UIText>
							<UIText
								sx={{
									width: 'auto',
									position: 'absolute',
									right: pxToRem(20),
									top: pxToRem(6),
									fontSize: pxToRem(14),
									lineHeight: pxToRem(20),
									color: color.colWhite,
									wordBreak: 'keep-all',
									whiteSpace: 'nowrap',
									display: {
										xs: viewPoint === 'article' ? 'none' : 'block',
										md: 'block',
									},
									opacity: 0.8,
								}}
							>
								{chartResultCount2} 표
							</UIText>
						</Box>
					</Box>
				</UIFlex>
				<UIFlex
					flexDirection="row"
					justifyContent="flex-end"
					gap={pxToRem(2)}
					sx={{
						marginTop: {
							xs: pxToRem(13),
							lg: pxToRem(17),
						},
						fontSize: pxToRem(14),
						lineHeight: pxToRem(14),
						color: '#082B4F',
						marginRight: {
							xs: pxToRem(16),
							md: 0,
						},
					}}
				>
					개표율 자세히 보기
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							marginTop: pxToRem(-1),
						}}
					>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							width="16"
							height="16"
							viewBox="0 0 16 16"
							fill="none"
						>
							<path d="M6 12L10 8L6 4" stroke="#242424" strokeWidth="1.5" />
						</svg>
					</Box>
				</UIFlex>
			</Box>
		</>
	)
}

export default VoteBanner
